import { useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { Sun, Moon, Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/features/auth/useAuth";
import { ApiError } from "@/api/client";
import { getUserFacingErrorMessage } from "@/api/userFacingError";
import { saveLastEmail, getLastEmail } from "@/features/auth/authService";
import { useTheme } from "@/hooks/useTheme";
import { PinDisplay, PinPad } from "@/components/auth/PinPad";
import { EloLinkMark } from "@/components/auth/EloLinkMark";
import { resolvePostLoginRedirect } from "./loginRedirect";
import "./LoginPage.css";

const PIN_LENGTH = 6;

export function LoginPage() {
  const navigate = useNavigate();
  const search = useSearch({ from: "/login" });
  const { loginWithPassword, loginWithPin } = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();

  const storedEmail = getLastEmail();
  const [tab, setTab] = useState<"password" | "pin">(
    storedEmail ? "pin" : "password",
  );
  const [email, setEmail] = useState(storedEmail ?? "");
  const [password, setPassword] = useState("");
  const [pin, setPin] = useState("");
  const [pinError, setPinError] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const redirectTo = (search as { redirect?: string }).redirect ?? "/";

  const passwordMutation = useMutation({
    mutationFn: () => loginWithPassword(email, password),
    onSuccess: () => {
      saveLastEmail(email);
      navigate({ to: resolvePostLoginRedirect(redirectTo) as "/" });
    },
    onError: (error) => {
      if (error instanceof ApiError && error.code === "account_deactivated") {
        navigate({ to: "/conta-desativada" });
      }
    },
  });

  const pinMutation = useMutation({
    mutationFn: (pinValue: string) =>
      loginWithPin(storedEmail ?? email, pinValue),
    onSuccess: () => {
      navigate({ to: resolvePostLoginRedirect(redirectTo) as "/" });
    },
    onError: (error) => {
      if (error instanceof ApiError && error.code === "account_deactivated") {
        navigate({ to: "/conta-desativada" });
        return;
      }
      setPinError(true);
      setTimeout(() => {
        setPinError(false);
        setPin("");
      }, 600);
    },
  });

  function submitPin() {
    if (pin.length === PIN_LENGTH && !pinMutation.isPending)
      pinMutation.mutate(pin);
  }

  function handleDigit(d: string) {
    if (pin.length < PIN_LENGTH && !pinMutation.isPending) {
      const newPin = pin + d;
      setPin(newPin);
      setPinError(false);
      if (newPin.length === PIN_LENGTH) pinMutation.mutate(newPin);
    }
  }

  function handleBackspace() {
    setPin((value) => value.slice(0, -1));
    setPinError(false);
  }

  function handlePasswordSubmit(event: React.FormEvent) {
    event.preventDefault();
    passwordMutation.mutate();
  }

  const isPending = passwordMutation.isPending || pinMutation.isPending;

  return (
    <div className={`login-page login-page--${theme}`}>
      <main className="login-page__form-side">
        <div className="login-page__content">
          <div className="login-page__brand">
            <EloLinkMark />
            <span>
              Elo<strong>Link</strong>
            </span>
          </div>
          <h1>Bem-vindo de volta</h1>
          <p className="login-page__subtitle">
            Acesse suas cotações e operações.
          </p>
          {storedEmail && (
            <div className="login-page__tabs" aria-label="Forma de acesso">
              <button
                type="button"
                aria-pressed={tab === "password"}
                onClick={() => setTab("password")}
              >
                Senha
              </button>
              <button
                type="button"
                aria-pressed={tab === "pin"}
                onClick={() => {
                  setPin("");
                  setPinError(false);
                  setTab("pin");
                }}
              >
                PIN
              </button>
            </div>
          )}
          <div key={tab} className="login-page__auth-content">
            {tab === "pin" && storedEmail ? (
              <div className="login-page__pin-mode">
                <p className="login-page__greeting">
                  Olá, <strong>{storedEmail}</strong>
                </p>
                <div
                  className={`login-page__pin-display${pinError ? " animate-shake" : ""}`}
                >
                  <PinDisplay
                    length={PIN_LENGTH}
                    filled={pin.length}
                    error={pinError}
                  />
                </div>
                {pinMutation.isError && !pinError && (
                  <p className="login-page__error" role="alert">
                    {getUserFacingErrorMessage(
                      pinMutation.error,
                      "PIN incorreto.",
                    )}
                  </p>
                )}
                <div className="login-page__pin-pad">
                  <PinPad
                    onDigit={handleDigit}
                    onBackspace={handleBackspace}
                    onConfirm={submitPin}
                    disabled={isPending}
                    active={tab === "pin"}
                  />
                </div>
                <button
                  type="button"
                  className="login-page__text-action"
                  onClick={() => setTab("password")}
                >
                  Usar senha
                </button>
              </div>
            ) : (
              <form
                className="login-page__password-form"
                onSubmit={handlePasswordSubmit}
              >
                <label htmlFor="email">E-mail</label>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  required
                  placeholder="seu@email.com"
                />
                <label htmlFor="password">Senha</label>
                <div className="login-page__password-field">
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    required
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    aria-label={
                      showPassword ? "Ocultar senha" : "Mostrar senha"
                    }
                    onClick={() => setShowPassword((value) => !value)}
                  >
                    {showPassword ? <EyeOff size={19} /> : <Eye size={19} />}
                  </button>
                </div>
                {passwordMutation.isError && (
                  <p className="login-page__error" role="alert">
                    {getUserFacingErrorMessage(
                      passwordMutation.error,
                      "Erro ao fazer login.",
                    )}
                  </p>
                )}
                <button
                  className="login-page__submit"
                  type="submit"
                  disabled={isPending}
                >
                  {isPending ? "Entrando..." : "Entrar"}
                </button>
              </form>
            )}
          </div>
        </div>
        <button
          type="button"
          className="login-page__theme"
          onClick={toggleTheme}
          aria-label={theme === "dark" ? "Modo claro" : "Modo escuro"}
          title={theme === "dark" ? "Modo claro" : "Modo escuro"}
        >
          {theme === "dark" ? <Sun size={20} /> : <Moon size={20} />}
        </button>
      </main>
      <aside className="login-page__art" aria-label="EloLink">
        <div className="login-page__orb">
          <EloLinkMark />
          <span>EloLink</span>
        </div>
        <strong>Uma operação mais clara.</strong>
        <p>Do primeiro pedido à última entrega.</p>
      </aside>
    </div>
  );
}
